// @flow

import * as React from 'react';
import { FormControl, InputLabel, FormHelperText } from '@material-ui/core';
import { Button } from '@material-ui/core';
import { Box } from 'react-system';
import { graphql, useFragment, useMutation } from 'react-relay';
import { useFormik, Form } from '@realadvisor/form';
import { useLocale } from '../../hooks/locale';
import { useRouter } from '../../controls/router';
import { InlineMarked } from '../../controls/marked';
import { BubbleBox, BubblesSeq } from '../../controls/bubble';
import { NumberInput } from '../../controls/number-input';
import { ComboDate } from '../../controls/combo-date';
import { SelectInput } from '../../controls/select-input';
import type { DevelopmentSecondBuyerStep_root$key } from './__generated__/DevelopmentSecondBuyerStep_root.graphql';
import type { DevelopmentSecondBuyerStep_form$key } from './__generated__/DevelopmentSecondBuyerStep_form.graphql';
import type {
  DevelopmentSecondBuyerStepUpsertFormMutation,
  DevelopmentRegistrationFormInput,
} from './__generated__/DevelopmentSecondBuyerStepUpsertFormMutation.graphql';

type Props = {|
  form: DevelopmentSecondBuyerStep_form$key,
  root: DevelopmentSecondBuyerStep_root$key,
|};

const useOptimisticUpsertForm = ({ form }) => {
  const [upsertForm, upserting] =
    useMutation<DevelopmentSecondBuyerStepUpsertFormMutation>(
      graphql`
        mutation DevelopmentSecondBuyerStepUpsertFormMutation(
          $input: UpsertDevelopmentRegistrationFormInput!
        ) {
          upsertDevelopmentRegistrationForm(input: $input) {
            form: developmentRegistrationForm {
              id
              ...DevelopmentSecondBuyerStep_form @relay(mask: false)
            }
          }
        }
      `,
    );

  const upsertOptimisticForm = (
    input: $Shape<DevelopmentRegistrationFormInput>,
    callback,
  ) => {
    const optimisticResponse = {
      upsertDevelopmentRegistrationForm: {
        form: {
          id: form.id,
          secondBuyerNationality: form.secondBuyerNationality,
          secondBuyerOccupation: form.secondBuyerOccupation,
          secondBuyerWorkPermit: form.secondBuyerWorkPermit,
          secondBuyerFirstName:
            input.secondBuyerFirstName ?? form.secondBuyerFirstName,
          secondBuyerLastName:
            input.secondBuyerLastName ?? form.secondBuyerLastName,
          secondBuyerDateOfBirth:
            input.secondBuyerDateOfBirth ?? form.secondBuyerDateOfBirth,
          secondBuyerPhoneNumber:
            input.secondBuyerPhoneNumber ?? form.secondBuyerPhoneNumber,
          secondBuyerSavings:
            input.secondBuyerSavings ?? form.secondBuyerSavings,
          secondBuyerAnnualRevenues:
            input.secondBuyerAnnualRevenues ?? form.secondBuyerAnnualRevenues,
          step: input.step ?? form.step,
        },
      },
    };
    upsertForm({
      variables: {
        input: {
          developmentRegistrationForm: { id: form.id, ...input },
        },
      },
      optimisticResponse,
      onCompleted: () => {
        callback?.();
      },
    });
  };

  return [upsertOptimisticForm, upserting];
};

const number_of_string = (string: string) => {
  const parsed = Number.parseFloat(string);
  if (Number.isNaN(parsed)) {
    return null;
  }
  return parsed;
};

const string_of_number = (number: ?number) => {
  if (number == null) {
    return '';
  }
  return String(number);
};

const SecondBuyerPersonalInformationSection = ({
  form,
  root,
  completed,
  onComplete,
}) => {
  const { t } = useLocale();
  const [optimisticUpsertForm] = useOptimisticUpsertForm({ form });

  const validate = values => {
    const errors = {};

    if (values.secondBuyerDateOfBirth == null) {
      errors.secondBuyerDateOfBirth = t('dateOfBirthErrorWithName', {
        firstName: form.secondBuyerFirstName ?? '',
        lastName: form.secondBuyerLastName ?? '',
      });
    }

    if (values.secondBuyerOccupationId == null) {
      errors.secondBuyerOccupationId = t('occupationErrorWithName', {
        firstName: form.secondBuyerFirstName ?? '',
        lastName: form.secondBuyerLastName ?? '',
      });
    }

    if (values.secondBuyerNationalityId == null) {
      errors.secondBuyerNationalityId = t('nationalityErrorWithName', {
        firstName: form.secondBuyerFirstName ?? '',
        lastName: form.secondBuyerLastName ?? '',
      });
    }

    if (
      values.secondBuyerNationalityId !==
        'TmF0aW9uYWxpdHk6Nzc5OWViYzYtMjZkMS0xMWU4LTgzNGQtZmJiNDg5ZjUxN2Ey' &&
      values.secondBuyerWorkPermitId == null
    ) {
      errors.secondBuyerWorkPermitId = t('workPermiErrorWithName', {
        firstName: form.secondBuyerFirstName ?? '',
        lastName: form.secondBuyerLastName ?? '',
      });
    }

    return errors;
  };

  const f = useFormik({
    initialValues: {
      secondBuyerDateOfBirth: form.secondBuyerDateOfBirth,
      secondBuyerOccupationId: form.secondBuyerOccupation?.id,
      secondBuyerNationalityId: form.secondBuyerNationality?.id,
      secondBuyerWorkPermitId: form.secondBuyerWorkPermit?.id,
    },
    validate,
    onSubmit: onComplete,
  });

  // changing select items on every render
  // will trigger controlled selectedItem change
  const occupationTypes = React.useMemo(() => {
    return (root.occupationTypes ?? []).flatMap(item => {
      return item ? [{ id: item.id, label: item.label }] : [];
    });
  }, [root.occupationTypes]);
  const nationalityTypes = React.useMemo(() => {
    return (root.nationalityTypes ?? []).flatMap(item => {
      return item ? [{ id: item.id, label: item.label }] : [];
    });
  }, [root.nationalityTypes]);
  const workPermitTypes = React.useMemo(() => {
    return (root.workPermitTypes ?? []).flatMap(item => {
      return item ? [{ id: item.id, label: item.label }] : [];
    });
  }, [root.workPermitTypes]);

  return (
    <BubblesSeq animate={!completed}>
      <BubbleBox>
        <InlineMarked>
          {t('pleaseGiveUsMoreDetailsOnTheSecondBuyer', {
            firstName: form.secondBuyerFirstName ?? '',
            lastName: form.secondBuyerLastName ?? '',
          })}
        </InlineMarked>
      </BubbleBox>

      <Form onSubmit={f.submitForm}>
        <Box py={2}>
          <FormControl
            variant="outlined"
            error={f.errors.secondBuyerDateOfBirth != null}
          >
            <InputLabel>{t('dateOfBirth')}</InputLabel>
            <ComboDate
              value={f.values.secondBuyerDateOfBirth}
              onChange={value => {
                f.setTouched({ secondBuyerDateOfBirth: true });
                f.setValues({ secondBuyerDateOfBirth: value });

                optimisticUpsertForm({
                  secondBuyerDateOfBirth: value,
                });
              }}
              //disabled={disabled}
            />
            {f.errors.secondBuyerDateOfBirth != null && (
              <FormHelperText>{f.errors.secondBuyerDateOfBirth}</FormHelperText>
            )}
          </FormControl>
        </Box>

        <Box py={2}>
          <FormControl
            variant="outlined"
            //disabled={disabled}
            error={f.errors.secondBuyerOccupationId != null}
          >
            <InputLabel>{t('profession')}</InputLabel>
            <SelectInput
              items={occupationTypes}
              value={f.values.secondBuyerOccupationId}
              onSelect={value => {
                f.setValues({ secondBuyerOccupationId: value });
                optimisticUpsertForm({ secondBuyerOccupationId: value });
              }}
              onBlur={() => f.setTouched({ secondBuyerOccupationId: true })}
            />
            {f.errors.secondBuyerOccupationId != null && (
              <FormHelperText>
                {f.errors.secondBuyerOccupationId}
              </FormHelperText>
            )}
          </FormControl>
        </Box>

        <Box py={2}>
          <FormControl
            variant="outlined"
            //disabled={disabled}
            error={f.errors.secondBuyerNationalityId != null}
          >
            <InputLabel>{t('nationality')}</InputLabel>
            <SelectInput
              items={nationalityTypes}
              value={f.values.secondBuyerNationalityId}
              onSelect={value => {
                f.setValues({ secondBuyerNationalityId: value });
                optimisticUpsertForm({ secondBuyerNationalityId: value });
              }}
              onBlur={() => f.setTouched({ secondBuyerNationalityId: true })}
            />
            {f.errors.secondBuyerNationalityId != null && (
              <FormHelperText>
                {f.errors.secondBuyerNationalityId}
              </FormHelperText>
            )}
          </FormControl>
        </Box>

        {!['CHE', null].includes(form.secondBuyerNationality?.name) && (
          <Box py={2}>
            <FormControl
              variant="outlined"
              //disabled={disabled}
              error={f.errors.secondBuyerWorkPermitId != null}
            >
              <InputLabel>{t('workPermit')}</InputLabel>
              <SelectInput
                items={workPermitTypes}
                value={f.values.secondBuyerWorkPermitId}
                onSelect={value => {
                  f.setValues({ secondBuyerWorkPermitId: value });
                  optimisticUpsertForm({ secondBuyerWorkPermitId: value });
                }}
                onBlur={() => f.setTouched({ secondBuyerWorkPermitId: true })}
              />
              {f.errors.secondBuyerWorkPermitId != null && (
                <FormHelperText>
                  {f.errors.secondBuyerWorkPermitId}
                </FormHelperText>
              )}
            </FormControl>
          </Box>
        )}

        {completed === false && (
          <Box py={2}>
            <Button color="primary" size="large" onClick={f.submitForm}>
              {t('next')}
            </Button>
          </Box>
        )}
      </Form>
    </BubblesSeq>
  );
};

const SecondBuyerAnnualRevenuesSection = ({ form, completed, onComplete }) => {
  const { t } = useLocale();
  const [optimisticUpsertForm] = useOptimisticUpsertForm({ form });

  const validate = values => {
    const errors = {};

    if (values.secondBuyerAnnualRevenues == null) {
      errors.secondBuyerAnnualRevenues = t('emptyRevenuesErrorWithName', {
        firstName: form.secondBuyerFirstName ?? '',
        lastName: form.secondBuyerLastName ?? '',
      });
    }

    return errors;
  };

  const f = useFormik({
    initialValues: {
      secondBuyerAnnualRevenues: form.secondBuyerAnnualRevenues,
    },
    validate,
    onSubmit: onComplete,
  });

  return (
    <BubblesSeq animate={!completed}>
      <BubbleBox>
        <InlineMarked>
          {t('pleaseGiveUsMoreDetailsOnTheFinancialSituationOfThesecondBuyer')}
        </InlineMarked>
      </BubbleBox>

      <Form onSubmit={f.submitForm}>
        <Box py={2}>
          <FormControl
            variant="outlined"
            error={f.errors.secondBuyerAnnualRevenues != null}
          >
            <InputLabel>{t('revenues')}</InputLabel>
            <NumberInput
              value={string_of_number(f.values.secondBuyerAnnualRevenues)}
              onChange={v => {
                const value = number_of_string(v);
                f.setValues({ secondBuyerAnnualRevenues: value });
                const valid =
                  validate({ secondBuyerAnnualRevenues: value })
                    .secondBuyerAnnualRevenues == null;
                optimisticUpsertForm({
                  secondBuyerAnnualRevenues: valid ? value : null,
                });
              }}
              maxLength={20}
              onBlur={() => f.setTouched({ secondBuyerAnnualRevenues: true })}
            />
            {f.errors.secondBuyerAnnualRevenues != null && (
              <FormHelperText>
                {f.errors.secondBuyerAnnualRevenues}
              </FormHelperText>
            )}
          </FormControl>
        </Box>

        {completed === false && (
          <Box py={2}>
            <Button color="primary" size="large" onClick={f.submitForm}>
              {t('next')}
            </Button>
          </Box>
        )}
      </Form>
    </BubblesSeq>
  );
};

const SecondBuyerSavingsSection = ({ form }) => {
  const { t } = useLocale();
  const router = useRouter();
  const [optimisticUpsertForm] = useOptimisticUpsertForm({ form });

  const validate = values => {
    const errors = {};

    if (values.secondBuyerSavings == null) {
      errors.secondBuyerSavings = t('emptySavingsError');
    }

    return errors;
  };

  const f = useFormik({
    initialValues: {
      secondBuyerSavings: form.secondBuyerSavings,
    },
    validate,
    onSubmit: () => {
      optimisticUpsertForm({ step: 'verification' }, () => {
        router.push({
          pathname: '/development-registration',
          query: { id: form.id, step: '3' },
        });
      });
    },
  });

  return (
    <BubblesSeq animate={form.step === 'second_buyer'}>
      <Form onSubmit={f.submitForm}>
        <Box py={2}>
          <FormControl
            variant="outlined"
            error={f.errors.secondBuyerSavings != null}
          >
            <InputLabel>{t('savings')}</InputLabel>
            <NumberInput
              value={string_of_number(f.values.secondBuyerSavings)}
              onChange={v => {
                const value = number_of_string(v);
                f.setValues({ secondBuyerSavings: value });
                const valid =
                  validate({ secondBuyerSavings: value }).secondBuyerSavings ==
                  null;
                optimisticUpsertForm({
                  secondBuyerSavings: valid ? value : null,
                });
              }}
              maxLength={20}
              onBlur={() => f.setTouched({ secondBuyerSavings: true })}
            />
            {f.errors.secondBuyerSavings != null && (
              <FormHelperText>{f.errors.secondBuyerSavings}</FormHelperText>
            )}
          </FormControl>
        </Box>

        <Box py={2}>
          <Button color="primary" size="large" onClick={f.submitForm}>
            {t('next')}
          </Button>
        </Box>
      </Form>
    </BubblesSeq>
  );
};

export const DevelopmentSecondBuyerStep = (props: Props): React.Node => {
  const root = useFragment(
    graphql`
      fragment DevelopmentSecondBuyerStep_root on Query {
        workPermitTypes: dictionaries(type: "work_permit_types") {
          id
          name
          label
        }
        occupationTypes: dictionaries(type: "occupation_types") {
          id
          name
          label
        }
        nationalityTypes: dictionaries(type: "nationality_types") {
          id
          name
          label
        }
      }
    `,
    props.root,
  );
  const form = useFragment(
    graphql`
      fragment DevelopmentSecondBuyerStep_form on DevelopmentRegistrationForm {
        id
        step
        secondBuyerFirstName
        secondBuyerLastName
        secondBuyerDateOfBirth
        secondBuyerPhoneNumber
        secondBuyerSavings
        secondBuyerAnnualRevenues
        secondBuyerNationality {
          id
          name
        }
        secondBuyerOccupation {
          id
        }
        secondBuyerWorkPermit {
          id
        }
      }
    `,
    props.form,
  );

  const [
    secondBuyerPersonalInformationSectionCompleted,
    setSecondBuyerPersonalInformationSectionCompleted,
  ] = React.useState(form.secondBuyerAnnualRevenues != null);
  const [
    secondBuyerAnnualRevenuesSectionCompleted,
    setSecondBuyerAnnualRevenuesSectionCompleted,
  ] = React.useState(form.secondBuyerAnnualRevenues != null);
  return (
    <>
      <SecondBuyerPersonalInformationSection
        root={root}
        form={form}
        completed={secondBuyerPersonalInformationSectionCompleted}
        onComplete={() => {
          setSecondBuyerPersonalInformationSectionCompleted(true);
        }}
      />
      {secondBuyerPersonalInformationSectionCompleted && (
        <SecondBuyerAnnualRevenuesSection
          form={form}
          root={root}
          completed={secondBuyerAnnualRevenuesSectionCompleted}
          onComplete={() => {
            setSecondBuyerAnnualRevenuesSectionCompleted(true);
          }}
        />
      )}
      {secondBuyerAnnualRevenuesSectionCompleted && (
        <SecondBuyerSavingsSection form={form} />
      )}
    </>
  );
};
